<template>
    <Head title="Meta Settings"/>
    <div class="w-full min-h-screen flex flex-col bg-gray-100 dark:bg-gray-500">
        <div class="bg-white dark:bg-gray-700 flex flex-col">
            <div class="flex flex-col flex-grow justify-center">
                <div class="max-w-7xl flex flex-grow flex-row pt-4 px-4 sm:pt-6 lg:px-8 items-center">
                    <div class="cursor-pointer" @click="back">
                        <ArrowLeftIcon class="h-7 w-7 mr-4 sm:h-8 sm:w-8 text-gray-500 dark:text-gray-100"/>
                    </div>
                    <div class="flex flex-col">
                        <h1 class="text-3xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100">Meta</h1>
                        <div class="py-2 whitespace-nowrap text-sm text-red-500">
                            These are dangerous settings. Proceed with caution
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex flex-col m-8 dark:text-white">
            <div class="mb-4 flex flex-row justify-between items-center">
                <h2 class="font-bold">Requests</h2>
            </div>
            <div class="flex flex-row rounded-lg shadow bg-gray-300 dark:bg-gray-600 text-gray-900 dark:text-gray-200 p-2">
                <div class="flex-grow mr-2">
                    <div class="font-bold">Delete <span class="text-red-500 font-bold">ALL</span> Requests with the status: {{ selectedStatus ? selectedStatus.name : status.name }} ({{ recordCount }} records)</div>
                    <button :disabled="recordCount === 0"
                        :class="[this.recordCount > 0 ? 'bg-red-500' : 'bg-gray-400']"
                        class=" text-white p-2 rounded-lg mt-2 w-full"
                        @click.prevent="openModal">
                        <span v-if="recordCount === 0">NO RECORDS TO </span>DELETE
                    </button>
                </div>
                <tailwind-select class="w-52" label="Status" @change="statusChanged" :options="statusOptions" :selected-item="selectedStatus" :dark-enabled="true"/>
            </div>
        </div>
    </div>
    <TransitionRoot appear :show="isDialogOpen" as="template">
        <Dialog as="div" @close="closeModal" class="relative z-20" :initialFocus="deleteButtonRef">
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black bg-opacity-25" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-y-auto">
                <div
                    class="flex min-h-full items-center justify-center p-4 text-center"
                >
                    <TransitionChild
                        as="template"
                        enter="duration-300 ease-out"
                        enter-from="opacity-0 scale-95"
                        enter-to="opacity-100 scale-100"
                        leave="duration-200 ease-in"
                        leave-from="opacity-100 scale-100"
                        leave-to="opacity-0 scale-95"
                    >
                        <div
                            class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                        >
                            <DialogTitle
                                as="h3"
                                class="text-lg font-medium leading-6 text-red-900"
                            >
                                Delete ALL requests with status <code>{{ selectedStatus ? selectedStatus.name : status.name }}</code>
                            </DialogTitle>
                            <div class="mt-2">
                                <p class="text-sm text-gray-500">
                                    This is <span class="text-red-500">irreversible</span>. Sure you want to delete <b class="text-red-500">ALL</b> (<b class="text-blue-500">{{ recordCount }}</b>) requests?
                                </p>
                                <div class="w-full rounded-full mt-4 bg-gray-100">
                                    <div :style="{'width': countDown + '%'}" :class="{'p-1' : countDown > 0}" class="rounded-full bg-red-500"></div>
                                </div>
                            </div>

                            <div class="mt-4 flex flex-row">
                                <span class="flex-grow"></span>
                                <button
                                    v-if="!busy"
                                    type="button"
                                    class="inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium"
                                    @click="closeModal"
                                >
                                    No
                                </button>
                                <button
                                    v-if="!busy"
                                    type="button"
                                    ref="deleteButtonRef"
                                    :disabled="countDown > 0"
                                    :class="{'bg-gray-200 text-gray-500': countDown > 0, 'hover:bg-red-200 text-red-900': countDown === 0}"
                                    class="inline-flex justify-center rounded-md border border-transparent bg-red-100 px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                                    @click="deleteRequests()"
                                >
                                    Yes
                                </button>
                                <svg v-if="busy" class="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                    <circle class="opacity-50" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="white" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import axios from "axios";
import Layout from '@/Shared/Layout'
import {isProxy, ref, toRaw} from "vue"
import { Head } from "@inertiajs/inertia-vue3";
import { ArrowLeftIcon } from "@heroicons/vue/solid";
import TailwindSelect from "@/Shared/Components/TailwindSelect";
import { Dialog, DialogTitle, TransitionRoot, TransitionChild } from "@headlessui/vue";

export default {
    layout: Layout,
    props: {
        count: Number,
        auth: Object,
        status: Object,
        statuses: Array,
    },
    components: {
        Head,
        ArrowLeftIcon,
        TailwindSelect,

        Dialog,
        DialogTitle,
        TransitionRoot,
        TransitionChild,
    },
    data() {
        return {
            busy: false,
            timer: null,
            countDown: 100,
        }
    },
    setup(props) {
        const isDialogOpen = ref(false);
        const deleteButtonRef = ref(null)

        return {
            isDialogOpen,
            deleteButtonRef,
            selectedStatus: props.statuses.filter(s => s.value === props.status)[0],
            recordCount: isProxy(props.count) ? toRaw(props.count) : props.count,
            statusOptions: isProxy(props.statuses) ? toRaw(props.statuses) : props.statuses,
        }
    },
    methods: {
        async statusChanged(e) {
            this.selectedStatus = e
            this.recordCount = '...';
            this.$forceUpdate();
            const response = await axios.get(this.$route('meta.count', {status: this.selectedStatus.value}))
            if (response.data && response.data.count != null) {
                this.recordCount = response.data.count
            }
            this.$forceUpdate();
        },
        closeModal() {
            this.isDialogOpen = false
            this.countDown = 100;
            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
            }
        },
        openModal() {
            const timeout = 5 //seconds;
            const updateInt = 10 //milliseconds;
            this.countDown -= 100 / (timeout * (1000 / updateInt));
            this.timer = setInterval(() => {
                if (this.countDown > 0) {
                    this.countDown -= 100 / (timeout * (1000 / updateInt));
                } else {
                    this.countDown = 0;
                    clearInterval(this.timer);
                }
            }, updateInt);
            this.isDialogOpen = true
        },
        async deleteRequests() {
            this.busy = true;
            const status = this.selectedStatus ? this.selectedStatus.value : this.status.value;

            const delResponse = await axios.delete(this.$route('meta.batch_delete_orders', {status: status}))
            if (delResponse.data && delResponse.data.message) {
                this.$page.props.flash = {
                    success: delResponse.data.message
                }
            }

            const response = await axios.get(this.$route('meta.count', {status: status}))
            if (response.data && response.data.count != null) {
                this.recordCount = response.data.count
            }

            this.closeModal();
            this.busy = false;
        }
    }
}
</script>
